import { FormattedMessage, useIntl } from 'react-intl';
import Button from './core/Button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/core/Dialog';
import { Dispatch, SetStateAction } from 'react';
import * as React from 'react';
import { cn } from '@/helpers/classnames';
import { IconXmark } from '@/icons';
import { useTenantID } from '@/hooks/useTenantID';

interface DialogWindowProps {
  dialogTrigger?: React.ReactNode;
  title: string;
  description?: string;
  descriptionClassName?: string;
  content: React.ReactNode;
  useFooter?: boolean;
  open?: boolean;
  onOpenChange?: Dispatch<SetStateAction<boolean>>;
  className?: string;
  preventClose?: boolean;
}

export const DialogWindow = ({
  dialogTrigger,
  title,
  description,
  descriptionClassName,
  content,
  open,
  onOpenChange,
  useFooter = false,
  className,
  preventClose = false,
}: DialogWindowProps) => {
  const tenantId = useTenantID();
  const { formatMessage } = useIntl();

  const handleOpenChange = (nextOpen: boolean) => {
    if (preventClose && !nextOpen) {
      return;
    }
    onOpenChange?.(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {dialogTrigger && <DialogTrigger asChild>{dialogTrigger}</DialogTrigger>}
      <DialogContent
        className={cn(
          'w-full min-w-0 max-w-panel-full gap-8 text-text-950',
          tenantId,
          className,
        )}
        onPointerDownOutside={
          preventClose ? (event) => event.preventDefault() : undefined
        }
        onEscapeKeyDown={
          preventClose ? (event) => event.preventDefault() : undefined
        }
        {...(!description && { 'aria-describedby': undefined })}
      >
        <DialogHeader>
          <div className="flex flex-row justify-between">
            <DialogTitle>{title}</DialogTitle>
            {!preventClose && (
              <DialogClose
                className="flex size-8 items-center justify-center transition-opacity hover:rounded-md hover:bg-background-200 disabled:pointer-events-none"
                aria-label={formatMessage({
                  defaultMessage: 'Stäng dialog',
                })}
              >
                <IconXmark className="size-5" />
              </DialogClose>
            )}
          </div>
        </DialogHeader>
        {description && (
          <DialogDescription className={descriptionClassName}>
            {description}
          </DialogDescription>
        )}
        <div className="w-full min-w-0">{content}</div>
        {useFooter && (
          <DialogFooter className="sm:justify-start">
            <DialogClose asChild>
              <Button variant="outline">
                <FormattedMessage defaultMessage="Stäng" />
              </Button>
            </DialogClose>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
